import { decode as decodeAswparam } from './aswparam.js';
import isEmptyObject from './isEmptyObject.js';
import isRegExp from './isRegExp.js';
import validateEmail from './validateEmail.js';

/**
 * Generic validation method
 * @param {string} value
 * @param {RegExp} regExp
 * @param {boolean} [condition=true]
 * @param {object} [data={}]
 * @returns {object}
 */
const validate = (value, regExp, condition = true, data = {}) => {
	const valueString = String(value);
	return {
		isValid: isRegExp(regExp) && regExp.test(valueString) && condition,
		value: valueString,
		regExp,
		data
	};
};
const helpers = {

	/**
	 * Luhn algorithm used to validate credit card numbers
	 * @param {string} cardNumber
	 * @returns {boolean}
	 */
	luhn(cardNumber) {
		const cardNumberString = String(cardNumber);
		if (/[^\d]/.test(cardNumberString)) {
			return false; // check if string input contains a number
		} else {
			return cardNumberString.split('').reverse().map((char) => Number(char)).reduce((sum, digit, counter) => { // start from the rightmost digit and move left
				if (counter % 2 === 0) {
					return sum + digit; // take the sum of all the digits.
				} else {
					const multipliedDigit = digit * 2; // double the value of every second digit
					return sum + (multipliedDigit > 9 ? multipliedDigit - 9 : multipliedDigit); // if the result of this doubling operation is greater than 9, then subtract 9 from that result
				}
			}, 0) % 10 === 0; // if the total modulo 10 is equal to 0 then the number is valid according to the Luhn formula
		}
	}

};
// if you add a new card type, please update the unit tests, too
const rules = {
	cardNumber: [{
		type: 'elo',
		typeRegExp: /^(?:401178|401179|431274|438935|451416|457393|457631|457632|504175|627780|636297|636368|655000|655001|651652|651653|651654|650485|650486|650487|650488|506699|5067[0-6]\d|50677[0-8]|509)/,
		cardRegExp: /^(?:401178|401179|431274|438935|451416|457393|457631|457632|504175|627780|636297|636368|655000|655001|651652|651653|651654|650485|650486|650487|650488|506699|5067[0-6]\d|50677[0-8]|509\d{3})\d{10}$/,
		codeRegExp: /^\d{3}$/
	}, {
		type: 'visa',
		typeRegExp: /^4/,
		cardRegExp: /^(4\d{15})$/,
		codeRegExp: /^\d{3}$/
	}, {
		type: 'master',
		typeRegExp: /^(?:5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)/,
		cardRegExp: /^(?:5[1-5]\d{2}|222[1-9]|22[3-9]\d|2[3-6]\d{2}|27[01]\d|2720)\d{12}$/,
		codeRegExp: /^\d{3}$/
	}, {
		type: 'american',
		typeRegExp: /^3[47]/,
		cardRegExp: /^3[47]\d{13}$/,
		codeRegExp: /^\d{4}$/
	}, {
		type: 'discover',
		typeRegExp: /^6(?:011|5)/,
		cardRegExp: /^6(?:011|5\d{2})\d{12}$/,
		codeRegExp: /^\d{3}$/
	}, {
		type: 'jcb',
		typeRegExp: /^(?:2131|1800|35)/,
		cardRegExp: /^(?:2131|1800|35\d{3})\d{11}$/,
		codeRegExp: /^\d{3}$/
	}, {
		type: 'hiperCard',
		typeRegExp: /^606282|3841/,
		cardRegExp: /^(606282\d{10}|606282\d{13}|3841\d{15})$/,
		codeRegExp: /^\d{3}$/
	}, {
		type: 'other',
		typeRegExp: /^\d/,
		cardRegExp: /^\d+$/,
		codeRegExp: /^\d{3}$/
	}]
};

/**
 * Validate aswparam
 * @param {string} aswparamString
 * @param {RegExp} [regExp]
 * @returns {object}
 */
validate.aswparam = (aswparamString, regExp = /^\S/) => {
	const decoded = decodeAswparam(aswparamString);
	const condition = !isEmptyObject(decoded);
	const validationResult = validate(aswparamString, regExp, condition);
	return validationResult;
};

/**
 * Validate credit card number
 * @param {string} cardNumber
 * @param {RegExp} [regExp]
 * @param {boolean} [condition]
 * @returns {object}
 */
validate.cardNumber = (cardNumber, regExp, condition = helpers.luhn(cardNumber)) => {
	const defaultRule = rules.cardNumber[rules.cardNumber.length - 1];
	if (isRegExp(regExp)) {
		return validate(cardNumber, regExp, condition, defaultRule);
	} else {
		const result = rules.cardNumber.find((item) => {
			return item.typeRegExp.test(cardNumber);
		}) || defaultRule;
		return validate(cardNumber, result.cardRegExp, condition, result);
	}
};

/**
 * Validate credit card code (CVC)
 * @param {string} cardCode
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.cardCode = (cardCode, regExp = /^\d{3}$/, condition = true) => {
	return validate(cardCode, regExp, condition);
};

/**
 * Validate credit card expiration date
 * @param {string} cardExpiration
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.cardExpiration = (cardExpiration, regExp = /^\d{2}\/\d{2}$/, condition = true) => {
	return validate(cardExpiration, regExp, condition);
};

/**
 * Validate email Address
 * @param {string} email
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.email = (email, regExp = /^[-\w!#$%&\\'*+/=?^`{|}~]+(?:\.[-\w!#$%&\\'*+/=?^`{|}~]+)*@(?:[a-zA-Z\d](?:[a-zA-Z\d-]*[a-zA-Z\d])?\.)+(?:[a-zA-Z\d][a-zA-Z\d-]*[a-zA-Z\d])+$/, condition = true) => {
	try {
		// Check characters in whole email
		validateEmail.checkAtSignInEmail(email);
		// Check recipient part of email
		validateEmail.checkEmailName(email);
		// Check domain name
		validateEmail.checkDomainName(email);
		// Check TLD (Top Level Domain)
		validateEmail.checkTldName(email);
	} catch (error) {
		return {
			data: error,
			isValid: false,
			regExp: error.regexp,
			value: error.value
		};
	}
	return validate(email, regExp, condition);
};

/**
 * Validate number
 * @param {string} number
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.number = (number, regExp = /^\d*(?:\.\d*)$/, condition = true) => {
	return validate(number, regExp, condition);
};

/**
 * Validate integer
 * @param {string} integer
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.integer = (integer, regExp = /^\d+$/, condition = true) => {
	return validate(integer, regExp, condition);
};

/**
 * Validate string
 * @param {string} string
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.string = (string, regExp = /^(?!^([aA][rR]{2}[aA][yY])$)(?:\S.{0,253}\S|\S)$/, condition = true) => {
	// Validate string shorter than 255 characters without whitespaces at the beginning or the end (255 - 2 = 253) and not contain "array" in any form
	return validate(string, regExp, condition);
};

/**
 * Validate tax (VAT)
 * @param {string} string
 * @param {string[]} [patterns]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.tax = (string, patterns = [], condition = true) => {
	const validationResults = patterns.map((pattern) => validate(string, new RegExp(pattern), condition));
	const validValidationResults = validationResults.filter((validationResult) => validationResult.isValid);
	return validValidationResults[0] || validationResults[0];
};

/**
 * Validate NationalId (Brazilian CPF/CNPJ numbers)
 * @param {string} string
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.nationalId = (string, regExp = /^(\d{11,14})$/, condition = true) => {
	// Remove any characters but digits before validating the string
	const clearedString = String(string).replace(/[^0-9]+/g, '');
	return validate(clearedString, regExp, condition);
};

/**
 * Validate phone number
 * @param {string} string
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.phoneNumber = (string, regExp = /^(\+?\d+(-*\d+)*)$/, condition = true) => {
	// Remove whitespaces before validating the string
	const clearedString = String(string).replace(/\s/g, '');
	return validate(clearedString, regExp, condition);
};

/**
 * Validate ZIP code
 * @param {string} string
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.zipCode = (string, regExp = /^.{1,32}$/, condition = true) => {
	const clearedString = String(string).replace(/\s/g, '');
	const matchedString = clearedString.match(regExp)?.at(0) ?? clearedString;
	return validate(matchedString, regExp, condition);
};

/**
 * Validate US ZIP code
 * @param {string} string
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.zipCodeUS = (string, regExp = /^\d{3,5}(-?\d{1,26})?$/, condition = true) => {
	return validate.zipCode(string, regExp, condition);
};

/**
 * Validate CA ZIP code
 * @param {string} string
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.zipCodeCA = (string, regExp = /^[a-zA-Z]\d[a-zA-Z]-?\d[a-zA-Z]\d$/, condition = true) => {
	return validate.zipCode(string, regExp, condition);
};

/**
 * Validate BR ZIP code
 * @param {string} string
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.zipCodeBR = (string, regExp = /^\d{5}-?\d{3}$/, condition = true) => {
	return validate.zipCode(string, regExp, condition);
};

/**
 * Validate IN ZIP code
 * @param {string} string
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.zipCodeIN = (string, regExp = /^[1-9]\d{5}$/, condition = true) => {
	return validate.zipCode(string, regExp, condition);
};

/**
 * Validate JP ZIP code
 * @param {string} string
 * @param {RegExp} [regExp]
 * @param {boolean} [condition=true]
 * @returns {object}
 */
validate.zipCodeJP = (string, regExp = /^\d{3}-?\d{4}$/, condition = true) => {
	return validate.zipCode(string, regExp, condition);
};

export default validate;
